import { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw';
import 'leaflet-draw/dist/leaflet.draw.css';

/**
 * Initializes a Leaflet map using CRS.Simple for flat plan image overlays.
 *
 * Unlike geographic maps, CRS.Simple uses a flat pixel coordinate system where
 * [0, 0] is the bottom-left and [imageHeight, imageWidth] is the top-right.
 * Leaflet's CRS.Simple uses [row, col] = [y, x] ordering (like lat/lng).
 *
 * @param {React.RefObject} containerRef - Ref to the DOM element that hosts the map.
 * @param {Object} options
 * @param {React.MutableRefObject} options.mapRef - Receives the L.Map instance.
 * @param {React.MutableRefObject} options.drawnItemsRef - Receives the L.FeatureGroup for drawings.
 * @param {string|null} options.imageUrl - URL of the plan image to overlay.
 * @param {number} options.imageWidth - Pixel width of the plan image.
 * @param {number} options.imageHeight - Pixel height of the plan image.
 */
export default function usePlanMapInit(containerRef, { mapRef, drawnItemsRef, imageUrl, imageWidth, imageHeight }) {
  const overlayRef = useRef(null);

  // Initialize the map once on mount
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;

    const map = L.map(containerRef.current, {
      crs: L.CRS.Simple,
      minZoom: -3,
      maxZoom: 5,
      zoomControl: true,
      attributionControl: false,
    });

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    mapRef.current = map;
    drawnItemsRef.current = drawnItems;

    setTimeout(() => {
      try { map.invalidateSize(); } catch (_) {}
    }, 100);

    return () => {
      try {
        map.remove();
      } catch (_) {}
      mapRef.current = null;
      drawnItemsRef.current = null;
      overlayRef.current = null;
    };
  }, []);

  // Swap the image overlay whenever imageUrl / dimensions change
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    // Remove previous overlay if present
    if (overlayRef.current) {
      try {
        map.removeLayer(overlayRef.current);
      } catch (_) {}
      overlayRef.current = null;
    }

    if (!imageUrl || !imageWidth || !imageHeight) return;

    // In CRS.Simple, bounds are [[y0, x0], [y1, x1]] which maps to [[0,0],[height,width]]
    const bounds = [[0, 0], [imageHeight, imageWidth]];

    const overlay = L.imageOverlay(imageUrl, bounds, {
      opacity: 1,
      interactive: false,
    }).addTo(map);

    overlayRef.current = overlay;

    // Fit the map view to show the full image
    map.fitBounds(bounds);
  }, [imageUrl, imageWidth, imageHeight, mapRef]);
}
